import React, { useRef, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Flex,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Icon,
  Tooltip,
  Radio,
  RadioGroup,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
} from "@chakra-ui/react";
import { 
  SearchIcon, 
  ChevronDownIcon, 
  DeleteIcon, 
  CopyIcon, 
  AddIcon,
  EditIcon
} from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { updatePlanHeader, deletePlan, searchPlansheaders } from "services/AnalysisPlanServices";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import CustomModal from "components/modals/CustomModal";
import { useNavigate } from "react-router-dom";
import { TbLayoutSidebarLeftCollapseFilled } from "react-icons/tb";
import { FaFilter } from "react-icons/fa6";

interface SingleListComponentProps {
  filteredHeaders: Header[];
  selectedPlanHeaders: Header[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onPlanSelection?: (header: Header) => void;
  onCreateNewPlan?: (planName: string) => Promise<void>;
  setPlanHeaders?: (headers: Header[]) => void;
  isSidebarVisible?: boolean;
  toggleSidebar?: () => void;
}

const SingleListComponent: React.FC<SingleListComponentProps> = ({
  filteredHeaders,
  selectedPlanHeaders,
  searchTerm,
  onSearchChange,
  onPlanSelection,
  onCreateNewPlan,
  setPlanHeaders,
  isSidebarVisible,
  toggleSidebar,
}) => {
  const [editingPlan, setEditingPlan] = useState<Header | null>(null);
  const [newPlanName, setNewPlanName] = useState("");
  const [deletingPlan, setDeletingPlan] = useState<Header | null>(null);
  const [cloningPlan, setCloningPlan] = useState<Header | null>(null);
  const [createPlanName, setCreatePlanName] = useState("");
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [dependencies, setDependencies] = useState<Header[]>([]);
  const [showDependenciesModal, setShowDependenciesModal] = useState(false);
  // Filter state
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">("all");
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  
  const { 
    isOpen: isRenameOpen,
    onOpen: onRenameOpen,
    onClose: onRenameClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);
  const { 
    clonePlan, 
    setPlan, 
    setNodeMap, 
    setSelectedNodeId, 
    setCurrentPlanHeader, 
    setQuestions
  } = useAnalysisPlanContext();
  const navigate = useNavigate();
  const handlePlanSelection = (header: Header) => {
    if (onPlanSelection) {
      onPlanSelection(header);
    }
  };

  const handleRenameClick = (header: Header) => {
    setEditingPlan(header);
    setNewPlanName(header.name || "");
    onRenameOpen();
  };

  const handleDeleteClick = (header: Header) => {
    setDeletingPlan(header);
    onDeleteOpen();
  };

  const handleCloneClick = async (header: Header) => {
    if (!header?.id) return;
    
    setCloningPlan(header);
    const newPlan = await clonePlan(header?.id, true /* save */); // Clone and save
    if (newPlan) {
      // Add the new plan to the list
      const updatedHeaders = [newPlan?.header!, ...filteredHeaders];
      setPlanHeaders?.(updatedHeaders);  
      // Navigate to the cloned plan.
      navigate(`/plan/${newPlan?.header?.id}`, { replace: true });
    }
    setCloningPlan(null);
  };

  const handleRenameSubmit = async () => {
    if (editingPlan && newPlanName.trim()) {
      const response = await updatePlanHeader(editingPlan?.id!, newPlanName.trim(), editingPlan?.description);
      if (response) {
        // Update the local state
        const updatedHeaders = filteredHeaders.map(header => 
          header?.id === editingPlan?.id 
            ? { ...header, name: newPlanName.trim() }
            : header
        );
        setPlanHeaders?.(updatedHeaders);
        // Update selected plan if it's the one being renamed
        if (selectedPlanHeaders.some(h => h.id === editingPlan?.id)) {
          const updatedSelectedHeaders = selectedPlanHeaders.map(h => 
            h.id === editingPlan?.id 
              ? { ...h, name: newPlanName.trim() }
              : h
          );
          if (onPlanSelection) {
            onPlanSelection(updatedSelectedHeaders[0]);
          }
        }
      }
    }
    onRenameClose();
    setEditingPlan(null);
    setNewPlanName("");
  };

  const handleDeleteSubmit = async () => {
    if (deletingPlan) {
      const response = await deletePlan(deletingPlan?.id!);
      if(response.ok){
        const updatedHeaders = filteredHeaders.filter(header => header?.id !== deletingPlan?.id);
        setPlanHeaders?.(updatedHeaders);
      } else if(response.length > 0){
        setDependencies(response);
        setShowDependenciesModal(true);
      }
    }
    onDeleteClose();
    setPlan(null);
    setSelectedNodeId(null);
    setCurrentPlanHeader(undefined);
    navigate("/plan");
    setNodeMap(new Map());
    setQuestions([]);
    setDeletingPlan(null);
  };

  const handleCreateNewPlan = () => {
    setCreatePlanName("");
    onCreateOpen();
  };

  const handleCreateSubmit = async () => {
    if (createPlanName.trim() && onCreateNewPlan) {
      setIsCreatingPlan(true);
      await onCreateNewPlan(createPlanName.trim());
      setIsCreatingPlan(false);
    }
    onCreateClose();
    setCreatePlanName("");
  };

  const fetchFilteredPlans = async (filterType: "all" | "active" | "inactive") => {
    try {
      let plans: {headers: any, totalRows: any} | null | undefined = undefined;
      
      if (filterType === "all") {
        // Fetch all plans without filtering
        plans = await searchPlansheaders(
          -1, // pageSize
          1,  // pageNumber
          "LAST_MODIFIED", // sortByField
          false, // sortAscending
          "", // searchString
          [], // metricIds
          true, // includeScheduleActivity
          ""  // activeSchedule
        );
      } else if (filterType === "active") {
        // Fetch active plans
        plans = await searchPlansheaders(
          -1, // pageSize
          1,  // pageNumber
          "LAST_MODIFIED", // sortByField
          false, // sortAscending
          "", // searchString
          [], // metricIds
          true, // includeScheduleActivity
          true  // activeSchedule
        );
      } else if (filterType === "inactive") {
        // Fetch inactive plans
        plans = await searchPlansheaders(
          -1, // pageSize
          1,  // pageNumber
          "LAST_MODIFIED", // sortByField
          false, // sortAscending
          "", // searchString
          [], // metricIds
          true, // includeScheduleActivity
          false  // activeSchedule
        );
      }
      
      if (plans && setPlanHeaders) {
        setPlanHeaders(plans.headers);
      }
    } catch (error) {
      console.error("Error fetching filtered plans:", error);
    }
  };

  const handleFilterChange = (newFilterType: "all" | "active" | "inactive") => {
    setFilterType(newFilterType);
    setIsFilterPopoverOpen(false);
    fetchFilteredPlans(newFilterType);
  };

  return (
    <Box
      height="100%"
      bg="gray.50"
      borderRight="1px"
      borderColor="gray.200"
      borderRadius="lg"
    >
      <VStack spacing={2} p={2} align="stretch" height="100%" overflowY="auto">
        {/* Search and Create New Plan Button */}
        <HStack spacing={0}>
         <IconButton
            aria-label="Collapse Sidebar"
            icon={<Icon as={TbLayoutSidebarLeftCollapseFilled} boxSize={7} />}
            variant="ghost"
            size="xs"
            color="gray.500"
            _hover={{ color: "purple.600", bg: "gray.50" }}
            cursor="pointer"
            m={0}
            p={0}
            onClick={toggleSidebar}
            isDisabled={selectedPlanHeaders.length === 0}
        />
        <HStack spacing={2} w="100%">
         
          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search plans..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              bg="white"
              size="sm"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)"
              }}
            />
          </InputGroup>
          <Button
            leftIcon={<AddIcon boxSize={3}/>}
            onClick={handleCreateNewPlan}
            size="xs"
            aria-label="Create New Plan"
            borderRadius="md"
            title="Create New Plan"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>

        </HStack>
          {/* AGENTS Heading with Filter */}
          <HStack justify="space-between" align="center" mx={1} p={0}>
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color="gray.500" 
              letterSpacing="wider"
              textTransform="uppercase"
              mb={0}
            >
              PLANS
            </Text>
            {/* Divider Line */}
            <Box 
              flex="1" 
              height="1px" 
              bg="gray.300" 
              mx={0}
            />
            <Popover 
              isOpen={isFilterPopoverOpen} 
              onClose={() => setIsFilterPopoverOpen(false)}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <IconButton
                  aria-label="Filter plans"
                  icon={<Icon as={FaFilter} />}
                  onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                  size="xs"
                  variant="ghost"
                  color={filterType !== "all" ? "purple.600" : "gray.500"}
                  bg={filterType !== "all" ? "purple.50" : "transparent"}
                  _hover={{ color: "purple.600", bg: filterType !== "all" ? "purple.100" : "gray.50" }}
                  cursor="pointer"
                />
              </PopoverTrigger>
              <PopoverContent w="180px">
                <PopoverArrow />
                <PopoverHeader fontWeight="bold" fontSize="sm" m={1}>
                  <Text align="left" m={0}>Filter Plans</Text>
                </PopoverHeader>
                <PopoverCloseButton size="xs" m={1} />
                <PopoverBody>
                  <RadioGroup value={filterType} onChange={handleFilterChange}>
                    <VStack align="start">
                      <HStack spacing={2}>
                        <Radio value="all" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>All Plans</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Radio value="active" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>Active Plans</Text>
                      </HStack>
                      <HStack spacing={2}>
                        <Radio value="inactive" size="sm" m={0}/>
                        <Text fontSize="sm" m={0}>Inactive Plans</Text>
                      </HStack>
                    </VStack>
                  </RadioGroup>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
        {/* Plan List */}
        <Box flex="1" overflowY="auto">
          <List spacing={2}>
            {filteredHeaders?.map((header) => (
              <ListItem key={header.id}>
                <Box
                  p={1}
                  bg={selectedPlanHeaders.some(h => h.id === header?.id) ? "purple.700" : "white"}
                  color={selectedPlanHeaders.some(h => h.id === header?.id) ? "white" : "gray.800"}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={selectedPlanHeaders.some(h => h.id === header?.id) 
                    ? { bg: "purple.700", color: "white" } 
                    : { bg: "purple.50", color: "gray.800" }}
                  transition="all 0.2s"
                  textAlign="left"
                  textOverflow="ellipsis"
                  maxWidth="100%"
                  borderWidth="1px"
                  borderColor="gray.300"
                >
                  <Flex justify="space-between" align="center">
                    <Box 
                      flex="1" 
                      onClick={() => handlePlanSelection(header)}
                      pr={2}
                      display="flex"
                      alignItems="center"
                      height="100%"
                    >
                      <Text fontSize="sm" noOfLines={1} mb={0} textAlign="left" ml={1} fontWeight="600">
                          {header.name || "Unnamed Plan"}
                      </Text>
                    </Box>
                    
                    {/* Active indicator */}
                    {header?.has_active_schedule && (
                      <Tooltip label="Active" placement="top">
                        <Box
                          w="12px"
                          h="12px"
                          borderRadius="full"
                          bg="red.500"
                          display="inline-block"
                          border="1.5px solid"
                          borderColor="bg.surface"
                          mr={2}
                        />
                      </Tooltip>
                    )}
                    
                    {/* Menu Button */}
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<ChevronDownIcon />}
                        variant="ghost"
                        size="sm"
                        color={selectedPlanHeaders.some(h => h.id === header?.id) ? "white" : "gray.600"}
                        _hover={{
                          bg: selectedPlanHeaders.some(h => h.id === header?.id) ? "purple.600" : "gray.100"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList 
                        bg="white" 
                        border="1px solid" 
                        borderColor="gray.200"
                        boxShadow="md"
                        minW="160px"
                      >
                        <MenuItem 
                          icon={<EditIcon color="gray.600" boxSize={4}/>}
                          onClick={() => handleRenameClick(header)}
                          fontSize="sm"
                          py={2}
                          px={3}
                          color="gray.800"
                          fontWeight="500"
                          _hover={{ bg: "gray.50" }}
                        >
                          Rename
                        </MenuItem>
                        <MenuItem 
                          icon={<CopyIcon color="gray.600" boxSize={4}/>}
                          onClick={() => handleCloneClick(header)}
                          isDisabled={cloningPlan?.id === header?.id}
                          fontSize="sm"
                          py={2}
                          px={3}
                          color="gray.800"
                          fontWeight="500"
                          _hover={{ bg: "gray.50" }}
                          _disabled={{ color: "gray.400", cursor: "not-allowed" }}
                        >
                          {cloningPlan?.id === header.id ? "Duplicating..." : "Duplicate"}
                        </MenuItem>
                        <MenuItem 
                          icon={<DeleteIcon color="red.500" boxSize={4}/>}
                          onClick={() => handleDeleteClick(header)}
                          fontSize="sm"
                          py={2}
                          px={3}
                          color="red.500"
                          _hover={{ bg: "red.50" }}
                          fontWeight="500"
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                </Box>
              </ListItem>
            ))}
            {filteredHeaders?.length === 0 && (
              <Center py={8}>
                <Text color="gray.500">
                  {searchTerm
                    ? "No plans found matching your search"
                    : "No plans available"}
                </Text>
              </Center>
            )}
          </List>
        </Box>
      </VStack>

      {/* Rename Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Enter new plan name"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  handleRenameSubmit();
                }
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={handleRenameSubmit}
              isDisabled={!newPlanName.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Plan
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "{deletingPlan?.name}"? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteSubmit} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Create New Plan Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Plan</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Enter plan name"
              value={createPlanName}
              onChange={(e) => setCreatePlanName(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  handleCreateSubmit();
                }
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={handleCreateSubmit}
              isDisabled={!createPlanName.trim()}
              isLoading={isCreatingPlan}
              loadingText="Creating..."
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Dependency Modal */}
      {showDependenciesModal && (
        <CustomModal
          isOpen={showDependenciesModal}
          onClose={() => setShowDependenciesModal(false)}
          title="Plan has following dependencies"
          size="lg"
          maxWidth="800px"
          maxHeight="600px"
        >
          <VStack spacing={4} align="start" p={4}>
            {/* <Text>This plan cannot be deleted because it has the following dependencies:</Text> */}
            {dependencies && (
              <Box width="100%">
                {dependencies.map((dependency) => (
                  <Text key={dependency.id} fontStyle="italic" fontWeight="bold">{dependency.name}</Text>
                ))}
              </Box>
            )}
          </VStack>
        </CustomModal>
      )}
    </Box>
  );
};

export default SingleListComponent; 